import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import Stats from "@/components/home/Stats";

const IMG = "/assets/founder.webp";

const CHAPTERS = [
  { num: "01", title: "Signal Over Noise", desc: "We present relevant, qualified and carefully screened candidates — never volume for the sake of volume." },
  { num: "02", title: "True Stakeholder Partnership", desc: "We work as an extension of your hiring team, close to hiring managers and business stakeholders, to understand the actual requirement." },
  { num: "03", title: "PAN India Reach", desc: "Sourcing networks across every major talent market in India, for both IT and Non-IT mandates." },
  { num: "04", title: "Confidentiality & Trust", desc: "The discretion, speed and stakeholder management expected in MNC and Fortune 500 recruitment environments." },
];

export default function About() {
  usePageMeta(
    "About Talentify | Recruitment & Talent Acquisition Company, India",
    "Talentify is a PAN India recruitment and talent acquisition company founded by a recruitment professional with 9+ years of experience across IT and Non-IT hiring for MNCs and Fortune 500 organisations."
  );
  return (
    <main>
      <section className="relative overflow-hidden pb-16 pt-40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% -20%, rgba(56,189,248,0.12), transparent 60%)" }} />
        <div className="relative mx-auto max-w-7xl px-6">
          <SectionHead overline="About Talentify" title="Talent That Moves Businesses Forward." testid="about-page-heading" />
          <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-2">
            <Reveal>
              <div className="space-y-5 text-base leading-relaxed text-slate-400">
                <p>
                  Talentify is a recruitment and talent acquisition company providing recruitment support to organisations across PAN India. Founded by a recruitment professional with 9 years of industry experience, Talentify brings deep expertise across IT and Non-IT hiring.
                </p>
                <p>
                  Our experience includes supporting recruitment requirements for MNCs and Fortune 500 organisations, giving us an understanding of the speed, quality, confidentiality and stakeholder management expected in modern recruitment.
                </p>
                <p>
                  We work as an extension of our clients' hiring teams, combining market understanding, targeted sourcing, candidate assessment and structured recruitment processes to deliver the right talent — across startups, mid-sized companies, MNCs and large enterprises.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="relative max-w-md overflow-hidden rounded-3xl border border-white/[0.08]">
                <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-[#030712]/80 via-transparent to-transparent" />
                <img src={IMG} alt="Founder of Talentify — recruitment and talent acquisition professional" className="aspect-[4/5] w-full object-cover object-top" loading="lazy" />
                <div className="absolute bottom-5 left-5 z-20">
                  <p className="font-display text-lg font-semibold text-white">Founder, Talentify</p>
                  <p className="text-xs text-slate-400">9+ years in recruitment &amp; talent acquisition</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative bg-[#060B18] py-24" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead overline="How We Work" title="The Talentify Standard" testid="manifesto-heading" />
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div data-testid={`manifesto-chapter-${c.num}`} className="group h-full bg-[#0A0F1D] p-9 transition-colors duration-500 hover:bg-[#0F172A]">
                  <p className="font-display text-5xl font-extrabold tracking-tight text-white/[0.07] transition-colors duration-500 group-hover:text-sky-400/20">{c.num}</p>
                  <h3 className="mt-4 font-display text-xl font-semibold text-white">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Stats />

      <section className="relative pb-28 pt-4">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="glass-card flex flex-col items-start justify-between gap-6 rounded-3xl p-10 sm:flex-row sm:items-center sm:p-12">
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white">Let's Build Your Next Team.</h3>
                <p className="mt-2 text-sm text-slate-400">Whether one critical position or an entire team — Talentify is ready.</p>
              </div>
              <Link
                to="/employers"
                data-testid="about-cta-hire-talent"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-blue-500"
              >
                Hire Talent <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
